import React, { useState } from 'react';

import PropTypes from 'prop-types';

import { useCurrentProjectId } from '@adsk/alloy-provider';
import { useLocale } from '@adsk/alloy-react-locale';
import {
  useNotifications,
  NOTIFICATION_STATUSES,
} from '@adsk/alloy-react-notification';

import {
  pluginShape,
  relationshipShape,
  createFormPropsShape,
} from './helpers/propTypes';
import ConnectedEntityPickerModal from './modal/ConnectedEntityPickerModal';

const ReferencePickerModalContainer = ({
  sourceEntityId,
  sourceEntityDomain,
  sourceEntityType,
  plugin,
  open,
  closeModal,
  onAddRelationships,
  relationshipsData,
  addEntitiesToCache,
  updateEntitiesInCache,
  container,
  createFormProps,
  dataViewProps,
}) => {
  const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
  const { showNotification } = useNotifications();
  const projectId = useCurrentProjectId();
  const [isSaving, setIsSaving] = useState(false);

  const handleAddRelationships = (sourceEntity, entitiesToLink) => {
    setIsSaving(true);

    const handleSave = () =>
      onAddRelationships(sourceEntity, entitiesToLink)
        .then(() => {
          closeModal();
          if (plugin.onAddRelationships) {
            plugin.onAddRelationships(sourceEntity, entitiesToLink, projectId);
          }
          setIsSaving(false);
        })
        .catch((error) => {
          console.error(
            'ReferencePicker - ReferencePickerModalContainer: Error adding relationships',
            error,
          );
          showNotification({
            status: NOTIFICATION_STATUSES.ERROR,
            message: T.translate('ERROR_ADDING_RELATIONSHIPS_MESSAGE', {
              context: entitiesToLink.length,
            }),
          });
          setIsSaving(false);
        });
    handleSave();
  };

  const onSave = (selectedEntities) => {
    const sourceEntity = {
      id: sourceEntityId,
      type: sourceEntityType,
      domain: sourceEntityDomain,
    };
    const entitiesToLink = selectedEntities.map((e) => ({
      id: e.id,
      type: plugin.metadata.type,
      domain: plugin.metadata.domain,
    }));
    handleAddRelationships(sourceEntity, entitiesToLink);
  };

  const onCreateSuccess = (entityOrEntities, { selectedEntities = [] }) => {
    const sourceEntity = {
      id: sourceEntityId,
      type: sourceEntityType,
      domain: sourceEntityDomain,
    };

    const entities = Array.isArray(entityOrEntities)
      ? entityOrEntities
      : [entityOrEntities];
    const allEntities = [...entities, ...selectedEntities];
    const entitiesToLink = allEntities.map((e) => ({
      id: e.id,
      type: plugin.metadata.type,
      domain: plugin.metadata.domain,
    }));
    handleAddRelationships(sourceEntity, entitiesToLink);
    addEntitiesToCache({
      entities,
      type: plugin.metadata.type,
      domain: plugin.metadata.domain,
    });
  };

  const onUpdate = (entity) => {
    updateEntitiesInCache({
      entities: [entity],
      type: plugin.metadata.type,
      domain: plugin.metadata.domain,
    });
  };

  const alreadyLinkedIds = (relationshipsData?.data || []).map(
    (r) => r.entityId,
  );

  const disabledIds = [...alreadyLinkedIds, sourceEntityId];

  return (
    <ConnectedEntityPickerModal
      open={open}
      sourceEntityType={sourceEntityType}
      plugin={plugin}
      closeModal={closeModal}
      container={container}
      markAsSelectedIds={alreadyLinkedIds}
      disabledIds={disabledIds}
      onSave={onSave}
      onCreateSuccess={onCreateSuccess}
      onUpdate={onUpdate}
      isSaving={isSaving}
      loading={relationshipsData.loading}
      error={relationshipsData.error}
      retry={relationshipsData.retry}
      allowCreate={true}
      multiple={true}
      createFormProps={createFormProps}
      dataViewProps={dataViewProps}
      addEntitiesToCache={addEntitiesToCache}
      updateEntitiesInCache={updateEntitiesInCache}
    />
  );
};

ReferencePickerModalContainer.propTypes = {
  /**
   * The entity ID we want to link to.
   */
  sourceEntityId: PropTypes.string.isRequired,
  /**
   * The source entity domain, as used by relationship service.
   */
  sourceEntityDomain: PropTypes.string.isRequired,
  /**
   * The source entity type, as used by relationship service.
   */
  sourceEntityType: PropTypes.string.isRequired,
  /**
   * The plugin to use for selected entity type.
   */
  plugin: pluginShape,
  /**
   * Whether or not the modal is open.
   */
  open: PropTypes.bool,
  /**
   * Handler for closing the modal.
   */
  closeModal: PropTypes.func,
  /**
   * Handler to add entity relationships. Should return a promise for add relationship request, resolving to the newly created relationships.
   * Receives two argument:
   * 1. The source entity to link all entities to, of shape: {domain, id, type};
   * 2. An array of entities to link to, of shape: [{domain, id, type}, ...]
   */
  onAddRelationships: PropTypes.func.isRequired,
  /**
   * The relationship data.
   */
  relationshipsData: PropTypes.shape({
    /** The parsed relationship data relevant for picker modal  */
    data: PropTypes.arrayOf(relationshipShape),
    /** Whether we currently load relationships */
    loading: PropTypes.bool,
    /** Whether fetching relationship data raised an error */
    error: PropTypes.bool,
    /** Retry loading relationships in case of an error. Required when `errorLoadingRelationships` is true */
    retry: PropTypes.func,
  }).isRequired,
  /**
   * A handle method to add entities to cache.
   */
  addEntitiesToCache: PropTypes.func,
  /**
   * A handle method to update entities to cache.
   */
  updateEntitiesInCache: PropTypes.func,
  /**
   * Modal container.
   */
  container: PropTypes.object,
  /**
   * Additional props to pass to create form.
   */
  createFormProps: createFormPropsShape,
  /**
   * Additional props to pass to DataView.
   */
  dataViewProps: PropTypes.object,
};

export default ReferencePickerModalContainer;
